import React, { useState, useEffect, useCallback } from 'react';
import { withMask } from 'use-mask-input';
import { AlertTriangle, Lightbulb, BellRing, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import InstallAppButton from '../../components/InstallAppButton';
import api from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/LoadingSpinner';

interface NotificationPreferences {
    id: number;
    email_on_new_lead: boolean;
    email_daily_summary: boolean;
    email_weekly_summary: boolean;
    daily_summary_time: string;
    weekly_summary_day: number;
    notify_only_winners: boolean;
    notify_wheels: number[];
    whatsapp_on_new_lead: boolean;
    whatsapp_number: string;
    push_on_new_lead: boolean;
}

interface Wheel {
    id: number;
    name: string;
}

interface WhatsAppStatus {
    exists: boolean;
    status: string;
}

const checkboxBase =
    'mt-1 h-5 w-5 rounded border-tinta/30 text-bordo focus:ring-bordo/40 focus:ring-offset-0 cursor-pointer';

const subFieldInput =
    'bg-white border border-tinta/15 rounded-lg px-3 py-2 text-sm text-tinta focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors';

const subFieldLabel =
    'block text-xs font-bold uppercase tracking-wider text-tinta mb-1.5';

const NotificationsPage: React.FC = () => {
    const { isFreePlan } = useAuth();
    const { pushStatus, enablePush } = useRealtime();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
    const [wheels, setWheels] = useState<Wheel[]>([]);
    const [whatsappStatus, setWhatsappStatus] = useState<WhatsAppStatus | null>(null);

    const weekDays = [
        { value: 0, label: 'Domingo' },
        { value: 1, label: 'Segunda-feira' },
        { value: 2, label: 'Terça-feira' },
        { value: 3, label: 'Quarta-feira' },
        { value: 4, label: 'Quinta-feira' },
        { value: 5, label: 'Sexta-feira' },
        { value: 6, label: 'Sábado' }
    ];

    const loadData = useCallback(async () => {
        try {
            setLoading(true);

            // Carregar preferências e rodas em paralelo
            const promises = [
                api.get('/notifications/preferences'),
                api.get('/wheels', { params: { limit: 1000 } })
            ];

            // Se não for plano free, verificar status do WhatsApp
            if (!isFreePlan) {
                promises.push(api.get('/whatsapp/connection/status'));
            }

            const responses = await Promise.all(promises);

            setPreferences(responses[0].data);
            setWheels(responses[1].data.wheels || []);

            if (!isFreePlan && responses[2]) {
                setWhatsappStatus(responses[2].data);
            }
        } catch (error: any) {
            console.error('Erro ao carregar dados:', error);
            toast.error('Erro ao carregar preferências');
        } finally {
            setLoading(false);
        }
    }, [isFreePlan]);

    useEffect(() => {
        loadData();
    }, [loadData]);


    const handleSave = async () => {
        if (!preferences) return;

        try {
            setSaving(true);

            const payload = {
                email_on_new_lead: preferences.email_on_new_lead,
                email_daily_summary: preferences.email_daily_summary,
                email_weekly_summary: preferences.email_weekly_summary,
                daily_summary_time: preferences.daily_summary_time,
                weekly_summary_day: preferences.weekly_summary_day,
                notify_only_winners: preferences.notify_only_winners,
                notify_wheels: preferences.notify_wheels,
                whatsapp_on_new_lead: preferences.whatsapp_on_new_lead,
                whatsapp_number: preferences.whatsapp_number,
                push_on_new_lead: preferences.push_on_new_lead ?? true
            }
            await api.put('/notifications/preferences', payload);

            toast.success('Preferências salvas com sucesso!');
        } catch (error: any) {
            console.error('Erro ao salvar preferências:', error);
            toast.error(error.response?.data?.error || 'Erro ao salvar preferências');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleWheel = (wheelId: number) => {
        if (!preferences) return;

        const notifyWheels = preferences.notify_wheels || [];
        const newNotifyWheels = notifyWheels.includes(wheelId)
            ? notifyWheels.filter(id => id !== wheelId)
            : [...notifyWheels, wheelId];

        setPreferences({
            ...preferences,
            notify_wheels: newNotifyWheels
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <LoadingSpinner />
            </div>
        );
    }

    if (!preferences) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="flex gap-3 p-4 rounded-lg bg-bordo/8 border border-bordo/30">
                    <AlertTriangle className="w-5 h-5 text-bordo mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-tinta leading-relaxed">
                        Erro ao carregar preferências de notificação
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="font-display text-3xl font-black text-tinta leading-tight">
                    Suas <span className="italic text-bordo">notificações</span>
                </h1>
                <p className="mt-2 text-sm text-tinta-soft">
                    Configure como você deseja ser notificado sobre novos leads
                </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-4 sm:p-6">
                <div className="space-y-6">
                    {/* Notificações push */}
                    <div className="pb-6 border-b border-tinta/10">
                        <h2 className="font-display text-lg font-bold text-tinta mb-4">
                            Notificações no celular e computador
                        </h2>

                        <label className="flex items-start space-x-3 cursor-pointer group">
                            <input
                                type="checkbox"
                                checked={preferences.push_on_new_lead ?? true}
                                onChange={(e) =>
                                    setPreferences({
                                        ...preferences,
                                        push_on_new_lead: e.target.checked
                                    })
                                }
                                className={checkboxBase}
                            />
                            <div className="flex-1">
                                <span className="block font-bold text-tinta group-hover:text-bordo">
                                    Avisar a cada novo cliente
                                </span>
                                <span className="block text-sm text-tinta-soft">
                                    Notificação instantânea nos dispositivos em que você ativar, mesmo com o app fechado
                                </span>
                            </div>
                        </label>

                        <div className="mt-4 ml-8">
                            {pushStatus === 'granted' && (
                                <p className="flex items-center gap-2 text-sm text-musgo font-semibold">
                                    <CheckCircle2 className="w-4 h-4" />
                                    Ativadas neste dispositivo
                                </p>
                            )}
                            {pushStatus === 'default' && (
                                <button
                                    onClick={enablePush}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-bordo hover:bg-bordo-dark text-creme text-sm font-bold rounded-lg transition-colors"
                                >
                                    <BellRing className="w-4 h-4" />
                                    Ativar neste dispositivo
                                </button>
                            )}
                            {pushStatus === 'denied' && (
                                <p className="text-sm text-tinta-soft">
                                    As notificações estão bloqueadas neste navegador. Libere nas configurações do site (ícone de cadeado ao lado do endereço).
                                </p>
                            )}
                            {pushStatus === 'needs-install' && (
                                <div className="max-w-xs">
                                    <p className="text-sm text-tinta-soft mb-2">
                                        No iPhone, instale o aplicativo na tela de início para receber notificações.
                                    </p>
                                    <InstallAppButton />
                                </div>
                            )}
                            {pushStatus === 'unsupported' && (
                                <p className="text-sm text-tinta-soft">
                                    Este navegador não suporta notificações push. Você continua vendo os avisos no sino do painel.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Notificações por Email */}
                    <div>
                        <h2 className="font-display text-lg font-bold text-tinta mb-4">
                            Notificações por Email
                        </h2>

                        <div className="space-y-4">
                            {/* Novo Lead */}
                            <label className="flex items-start space-x-3 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={preferences.email_on_new_lead}
                                    onChange={(e) =>
                                        setPreferences({
                                            ...preferences,
                                            email_on_new_lead: e.target.checked
                                        })
                                    }
                                    className={checkboxBase}
                                />
                                <div className="flex-1">
                                    <span className="block font-bold text-tinta group-hover:text-bordo">
                                        Notificar em tempo real
                                    </span>
                                    <span className="block text-sm text-tinta-soft">
                                        Receber email imediatamente quando um novo lead for capturado
                                    </span>
                                </div>
                            </label>

                            {/* Resumo Diário */}
                            <label className="flex items-start space-x-3 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={preferences.email_daily_summary}
                                    onChange={(e) =>
                                        setPreferences({
                                            ...preferences,
                                            email_daily_summary: e.target.checked
                                        })
                                    }
                                    className={checkboxBase}
                                />
                                <div className="flex-1">
                                    <span className="block font-bold text-tinta group-hover:text-bordo">
                                        Resumo diário
                                    </span>
                                    <span className="block text-sm text-tinta-soft">
                                        Receber um resumo dos leads capturados no dia
                                    </span>

                                    {preferences.email_daily_summary && (
                                        <div className="mt-3 pl-4 border-l-2 border-bordo/30">
                                            <label className={subFieldLabel}>
                                                Horário do envio
                                            </label>
                                            <input
                                                type="time"
                                                value={preferences.daily_summary_time.substring(0, 5)}
                                                onChange={(e) =>
                                                    setPreferences({
                                                        ...preferences,
                                                        daily_summary_time: e.target.value + ':00'
                                                    })
                                                }
                                                className={subFieldInput}
                                            />
                                        </div>
                                    )}
                                </div>
                            </label>

                            {/* Resumo Semanal */}
                            <label className="flex items-start space-x-3 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={preferences.email_weekly_summary}
                                    onChange={(e) =>
                                        setPreferences({
                                            ...preferences,
                                            email_weekly_summary: e.target.checked
                                        })
                                    }
                                    className={checkboxBase}
                                />
                                <div className="flex-1">
                                    <span className="block font-bold text-tinta group-hover:text-bordo">
                                        Resumo semanal
                                    </span>
                                    <span className="block text-sm text-tinta-soft">
                                        Receber um resumo dos leads capturados na semana
                                    </span>

                                    {preferences.email_weekly_summary && (
                                        <div className="mt-3 pl-4 border-l-2 border-bordo/30">
                                            <label className={subFieldLabel}>
                                                Dia do envio
                                            </label>
                                            <select
                                                value={preferences.weekly_summary_day}
                                                onChange={(e) =>
                                                    setPreferences({
                                                        ...preferences,
                                                        weekly_summary_day: parseInt(e.target.value)
                                                    })
                                                }
                                                className={subFieldInput}
                                            >
                                                {weekDays.map((day) => (
                                                    <option key={day.value} value={day.value}>
                                                        {day.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </label>
                        </div>
                    </div>

                    <hr className="border-tinta/10" />

                    {/* Notificações via WhatsApp (apenas Pro) */}
                    {!isFreePlan && (
                        <>
                            <div>
                                <h2 className="font-display text-lg font-bold text-tinta mb-4">
                                    Notificações via WhatsApp
                                </h2>

                                {whatsappStatus?.status === 'connected' ? (
                                    <div className="space-y-4">
                                        <label className="flex items-start space-x-3 cursor-pointer group">
                                            <input
                                                type="checkbox"
                                                checked={preferences.whatsapp_on_new_lead}
                                                onChange={(e) =>
                                                    setPreferences({
                                                        ...preferences,
                                                        whatsapp_on_new_lead: e.target.checked
                                                    })
                                                }
                                                className={checkboxBase}
                                            />
                                            <div className="flex-1">
                                                <span className="block font-bold text-tinta group-hover:text-bordo">
                                                    Notificar por WhatsApp
                                                </span>
                                                <span className="block text-sm text-tinta-soft">
                                                    Receber mensagem WhatsApp quando um novo lead for capturado
                                                </span>

                                                {preferences.whatsapp_on_new_lead && (
                                                    <div className="mt-3 pl-4 border-l-2 border-bordo/30">
                                                        <label className={subFieldLabel}>
                                                            Número do WhatsApp (com DDI)
                                                        </label>
                                                        <input
                                                            ref={withMask('+99 (99) 99999-9999')}
                                                            value={preferences.whatsapp_number || ''}
                                                            onChange={(e) =>
                                                                setPreferences({
                                                                    ...preferences,
                                                                    whatsapp_number: e.target.value
                                                                })
                                                            }
                                                            placeholder="+55 (11) 99999-9999"
                                                            className={`${subFieldInput} w-full md:w-64`}
                                                        />
                                                        <p className="mt-1.5 text-xs text-tinta-soft italic">
                                                            Exemplo: +55 (11) 99999-9999 (Brasil com código do país)
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </label>
                                    </div>
                                ) : (
                                    <div className="flex items-start gap-3 p-4 rounded-lg bg-mostarda/15 border border-mostarda/40">
                                        <AlertTriangle className="w-5 h-5 text-mostarda-dark mt-0.5 flex-shrink-0" />
                                        <div className="flex-1">
                                            <h3 className="font-display font-bold text-tinta mb-1">
                                                WhatsApp não conectado
                                            </h3>
                                            <p className="text-sm text-tinta-soft mb-3">
                                                Para receber notificações via WhatsApp, você precisa conectar sua conta primeiro.
                                            </p>
                                            <a
                                                href="/admin/whatsapp"
                                                className="inline-block px-4 py-2 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark transition-colors"
                                            >
                                                Conectar WhatsApp
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <hr className="border-tinta/10" />
                        </>
                    )}

                    {/* Filtros de Notificação */}
                    <div>
                        <h2 className="font-display text-lg font-bold text-tinta mb-4">
                            Filtros de Notificação
                        </h2>

                        <div className="space-y-4">
                            {/* Apenas Vencedores */}
                            <label className="flex items-start space-x-3 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={preferences.notify_only_winners}
                                    onChange={(e) =>
                                        setPreferences({
                                            ...preferences,
                                            notify_only_winners: e.target.checked
                                        })
                                    }
                                    className={checkboxBase}
                                />
                                <div className="flex-1">
                                    <span className="block font-bold text-tinta group-hover:text-bordo">
                                        Notificar apenas vencedores
                                    </span>
                                    <span className="block text-sm text-tinta-soft">
                                        Receber notificações somente quando um lead ganhar um prêmio
                                    </span>
                                </div>
                            </label>

                            {/* Rodas Específicas */}
                            <div>
                                <label className="block font-bold text-tinta mb-2">
                                    Rodas para notificar
                                </label>
                                <p className="text-sm text-tinta-soft mb-3">
                                    Selecione as rodas que você deseja receber notificações. Se nenhuma for
                                    selecionada, você receberá de todas.
                                </p>

                                {wheels.length === 0 ? (
                                    <div className="bg-creme-light text-tinta-soft italic p-3 rounded-lg border border-tinta/10 text-sm">
                                        Você ainda não criou nenhuma roda de prêmios
                                    </div>
                                ) : (
                                    <div className="max-h-60 overflow-y-auto bg-creme-light border border-tinta/15 rounded-lg p-3 space-y-1">
                                        {wheels.map((wheel) => (
                                            <label
                                                key={wheel.id}
                                                className="flex items-center space-x-2 cursor-pointer hover:bg-white p-2 rounded transition-colors"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={
                                                        preferences.notify_wheels.length === 0 ||
                                                        preferences.notify_wheels.includes(wheel.id)
                                                    }
                                                    onChange={() => handleToggleWheel(wheel.id)}
                                                    className="h-4 w-4 rounded border-tinta/30 text-bordo focus:ring-bordo/40 focus:ring-offset-0 cursor-pointer"
                                                />
                                                <span className="text-sm text-tinta">{wheel.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer com botão de salvar */}
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-6 py-2.5 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {saving ? 'Salvando...' : 'Salvar Preferências'}
                    </button>
                </div>
            </div>

            {/* Info Box */}
            <div className="flex items-start gap-3 p-4 rounded-lg bg-tinta/5 border border-tinta/15">
                <Lightbulb className="w-5 h-5 text-mostarda-dark mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                    <h3 className="font-display font-bold text-tinta mb-1">Dica</h3>
                    <p className="text-sm text-tinta-soft">
                        As notificações em tempo real ajudam você a responder rapidamente aos seus
                        leads e aumentar suas conversões. Configure o horário dos resumos de acordo
                        com sua rotina!
                    </p>
                </div>
            </div>
        </div>
    );
};

export default NotificationsPage;
